import { useEffect } from "react";
import api from "@/lib/api";
import { flushOfflineQueue } from "@/lib/offlineQueue";
import { toast } from "sonner";

const DAY_KEY = "psc_selfcheck_day";
const today = () => new Date().toISOString().slice(0, 10);
const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

const pingBackend = async () => {
  for (let attempt = 0; attempt < 5; attempt++) {
    try {
      const r = await api.get("/health", { timeout: 15000 });
      return r.data;
    } catch {
      await sleep(2000 * (attempt + 1));
    }
  }
  return null;
};

export const runDailyCheck = async () => {
  const results = { server: false, db: false, session: null, synced: 0, updated: false };

  const health = await pingBackend();
  results.server = !!health?.ok;
  results.db = !!health?.db;

  if (localStorage.getItem("psc_token")) {
    try { await api.get("/auth/me", { timeout: 15000 }); results.session = true; }
    catch (e) { results.session = e?.response?.status === 401 ? false : null; }
  }

  try { results.synced = (await flushOfflineQueue()).flushed; } catch { /* noop */ }

  try {
    const reg = await navigator.serviceWorker?.getRegistration();
    if (reg) { await reg.update(); results.updated = true; }
  } catch { /* noop */ }

  return results;
};

export const DailyCheck = () => {
  useEffect(() => {
    if (localStorage.getItem(DAY_KEY) === today()) return;
    let cancelled = false;
    const t = setTimeout(async () => {
      const res = await runDailyCheck();
      if (cancelled) return;
      if (res.server && res.db && res.session !== false) {
        localStorage.setItem(DAY_KEY, today());
        const extras = res.synced > 0 ? ` · ${res.synced} offline draft${res.synced > 1 ? "s" : ""} synced` : "";
        toast.success(`Daily check complete — server ✓ database ✓ sign-in ✓${extras}`, { duration: 5000 });
      } else if (!res.server) {
        toast.warning("Daily check: server is still waking up — it may take another moment. Your work saves locally either way.", { duration: 8000 });
      } else if (!res.db) {
        toast.warning("Daily check: database not responding yet — retrying on next open. Local drafts are safe.", { duration: 8000 });
      } else if (res.session === false) {
        localStorage.setItem(DAY_KEY, today());
        toast.warning("Daily check: your session has expired — please sign in again. Unsaved work is kept on this device.", { duration: 8000 });
      }
    }, 1200);
    return () => { cancelled = true; clearTimeout(t); };
  }, []);
  return null;
};

export default DailyCheck;
